/**
 * Analytics API
 */

import { apiFetch } from './config';

export interface ActivitySummary {
  harvests: { total: number; by_day: Record<string, number>[] };
  injections: { total: number; by_day: Record<string, number>[] };
  promotions: { proposed: number; active: number; events: Record<string, number>[] };
  violations: { total: number; by_category: Record<string, number> };
  top_standards: { standard_name: string; count: number }[];
}

export interface ConvergenceStandard {
  standard_id: string;
  standard_name: string;
  adoption_rate: number;
  developers_using: number;
  total_injections: number;
  avg_relevance: number;
}

export interface ConvergenceDeveloper {
  email: string;
  display_name: string;
  unique_standards: number;
  total_injections: number;
  alignment_score: number;
}

export interface ConvergenceTrend {
  week: string;
  score: number;
  standards_count: number;
  developer_count: number;
}

export interface ConvergenceData {
  convergence_score: number;
  trend: 'improving' | 'stable' | 'declining';
  developer_count: number;
  standards_in_use: number;
  by_standard: ConvergenceStandard[];
  by_developer: ConvergenceDeveloper[];
  convergence_trend: ConvergenceTrend[];
}

export const analyticsApi = {
  /**
   * Get developer quality scores and insights
   */
  getDeveloperScores: async (projectId: string, period: string) => {
    const params = new URLSearchParams({ project_id: projectId, period });
    return apiFetch<any>(`/api/analytics/developer-scores?${params}`);
  },

  /**
   * Get LLM-powered coaching recommendations for a developer
   */
  getCoaching: async (projectId: string, userEmail: string) => {
    const params = new URLSearchParams({ project_id: projectId, user_id: userEmail });
    return apiFetch<any>(`/api/analytics/coaching?${params}`);
  },

  /**
   * Get activity summary (standards injections, violations, patterns)
   */
  getActivitySummary: async (period: string = '30d', projectId?: string) => {
    const params = new URLSearchParams({ period });
    if (projectId) params.append('project_id', projectId);
    const result = await apiFetch<any>(`/api/analytics/activity-summary?${params}`);
    if (result.success && result.data) {
      const data = result.data?.data || result.data;
      return { success: true, data: data as ActivitySummary };
    }
    return result;
  },

  /**
   * Get team convergence analytics - standards adoption alignment
   */
  getConvergence: async (period: string = '30d', companyId?: string) => {
    const params = new URLSearchParams({ period });
    if (companyId) params.append('Company_ID', companyId);
    const result = await apiFetch<any>(`/api/analytics/convergence?${params}`);
    if (result.success && result.data) {
      const data = result.data?.data || result.data;
      return { success: true, data: data as ConvergenceData };
    }
    return result;
  },
};
